import React, { useEffect, useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import moment from 'moment'
import { statusOptions } from '../../utils/fieldForms'
import { dataActions } from '../../store/data'
import { sortBy } from '../../utils/global'
import * as ApiServices from '../../services/ApiService'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  FormControl,
  IconButton,
  Box,
  Typography,
  Modal,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
  Button
} from '@mui/material'

import { RoundedButton } from '../../styles/mui_custom_components'

import { modalFormStyles } from '../../styles/classes/FieldFormClasses'
import { useTranslation } from 'react-i18next'
import { menuProps } from '../../styles/mui_custom_theme'
import customTheme from '../../styles/mui_theme'
import { auditOptions, auditType } from '../../utils/formBuilder'

const defaultObject = {
  name: 'Default'
}

const NewFormModal = props => {
  const currentUser = useSelector(state => state.auth.user)
  const screenConfigsList = useSelector(state => state.data.screenConfigsList)
  const tradesFromStore = useSelector(state => state.data.trades)
  const woTypesFromStore = useSelector(state => state.data.workOrderTypes)

  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const modalClasses = modalFormStyles().classes
  const [tradeList, setTradeList] = useState([{ ...defaultObject }])
  const [serviceList, setServiceList] = useState([{ ...defaultObject }])
  const [typeList, setTypeList] = useState([{ ...defaultObject }])

  // form values
  const [woType, setWoType] = useState(null)
  const [service, setService] = useState(null)
  const [trade, setTrade] = useState(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [showAssignmentTip, setShowAssignmentTip] = useState(false)

  useEffect(() => {
    init()
  }, [tradesFromStore, woTypesFromStore])

  const init = async () => {
    if (tradesFromStore) {
      setTradeList([defaultObject, ...tradesFromStore])
    }
    if (woTypesFromStore) {
      setTypeList([defaultObject, ...woTypesFromStore])
    }
  }

  // modal form functions
  const woTypeChangeHandler = event => {
    setWoType(event.target.value)
  }

  const tradeChangeHandler = event => {
    setTrade(event.target.value)
    setService(null)
    if (event?.target?.value?.trade_services) {
      const tradeServices = [...event?.target?.value?.trade_services]
      tradeServices.sort((a, b) => sortBy(a, b, 'name'))
      setServiceList([defaultObject, ...tradeServices])
    }
  }

  const serviceChangeHandler = event => {
    setService(event.target.value)
  }

  const formNameHandler = event => {
    setFormName(event.target.value)
  }

  const formDescriptionHandler = event => {
    setFormDescription(event.target.value)
  }

  const helpTipHandler = () => {
    setShowAssignmentTip(prevState => !prevState)
  }

  // next handler (modal)
  const nextHandler = async () => {
    await newScreenConfigurationHandler()
    // navigate to edit screen
    history.push('/form-builder')
  }

  const cancelHandler = () => {
    props.onClose()
    setShowAssignmentTip(false)
    setTrade(null)
    setService(null)
    setWoType(null)
    setFormName('')
    setFormDescription('')
  }

  const newScreenConfigurationHandler = async () => {
    const tradeName = trade?.name
    const serviceName = service?.name
    const typeName = woType?.name
    const unixTime = moment().unix()
    const sectionNumberId = Math.round(Math.random() * Date.now()).toString(16)
    // create page and section structure
    const sectionKey = `section_${sectionNumberId}`
    const configurationsObject = [
      {
        page_title: t('form_builder.page_one'),
        page_questions: [
          {
            [sectionKey]: {
              title: t('form_builder.section_one'),
              questions: []
            }
          }
        ]
      }
    ]

    const objectToCreate = {
      form_name: formName,
      form_description: formDescription,
      status: statusOptions.DRAFT, // created as draft
      type: 'eq_services',
      trade: tradeName,
      service: serviceName,
      work_type: typeName,
      last_modified: {
        user_id: currentUser?.userId,
        date: unixTime,
        firstName: currentUser?.userInfo?.firstName,
        lastName: currentUser?.userInfo?.lastName
      },
      config_id: currentUser?.userInfo?.config_id,
      configurations: configurationsObject
    }
    let newScreenConfiguration = null
    newScreenConfiguration = await ApiServices.createScreenConfiguration(
      objectToCreate
    )
    if (newScreenConfiguration) {
      // create audit trail
      await createNewAudit(auditOptions.DRAFT, newScreenConfiguration.id)
      // add the new Screen configuration to store
      dispatch(dataActions.setCurrentScreenConfig(newScreenConfiguration))
      // add the new Screen configuration to the store list
      dispatch(
        dataActions.setScreenConfigsList([
          newScreenConfiguration,
          ...screenConfigsList
        ])
      )
    }
  }

  const createNewAudit = async (auditOption, screenConfigId) => {
    const unixTime = moment().unix()
    const auditToCreate = {
      user_id: currentUser?.userId,
      date: unixTime,
      type: auditType,
      change: {
        status: auditOption
      },
      first_name: currentUser?.userInfo?.firstName,
      last_name: currentUser?.userInfo?.lastName,
      record_id: screenConfigId
    }
    await ApiServices.createAudit(auditToCreate)
  }

  return (
    <Modal
      open={props.open}
      onClose={cancelHandler}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={customTheme.modalStyle}>
        <Typography
          className={modalClasses.titleText}
          id="modal-modal-title"
          variant="h6"
          component="h2"
        >
          {t('field_forms.new_field_form')}
        </Typography>
        <FormControl className={modalClasses.customWidth}>
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.form_name')}
            </Typography>
          </Box>
          <TextField
            multiline
            className={modalClasses.fields}
            id="form_name"
            variant="outlined"
            placeholder={t('field_forms.form_name_placeholder')}
            value={formName}
            onChange={formNameHandler}
          />
        </FormControl>
        {/* Description */}
        <FormControl className={modalClasses.customWidth}>
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.description')}
            </Typography>
          </Box>
          <TextField
            value={formDescription}
            multiline
            className={modalClasses.formDescription}
            id="form_name"
            variant="outlined"
            placeholder={t('field_forms.description_placeholder')}
            rows={4}
            onChange={formDescriptionHandler}
          />
        </FormControl>
        <Box>
          <Box className={modalClasses.topContainer}>
            <Typography
              className={modalClasses.titleText}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              {t('field_forms.work_order_assignment')}
            </Typography>
            <IconButton aria-label="delete" onClick={helpTipHandler}>
              <InfoOutlinedIcon className={modalClasses.infoIcon} />
            </IconButton>
          </Box>
          {showAssignmentTip && (
            <FormHelperText className={modalClasses.formHelperTextStyle}>
              {t('field_forms.assignment_tip')}
            </FormHelperText>
          )}
        </Box>
        {/* Trade */}
        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.trade')}
            </Typography>
          </Box>

          <Select
            MenuProps={menuProps}
            className={modalClasses.selectStyle}
            value={trade?.name ?? ''}
            onChange={tradeChangeHandler}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_trade')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {tradeList.map(trade => (
              <MenuItem
                key={`${trade?.id}-${trade?.name}`}
                value={trade}
                className={modalClasses.itemsFontStyle}
              >
                {trade?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.service')}
            </Typography>
          </Box>

          <Select
            MenuProps={menuProps}
            disabled={!trade}
            className={modalClasses.selectStyle}
            value={service?.name ?? ''}
            onChange={serviceChangeHandler}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_service')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {serviceList.map(service => (
              <MenuItem
                key={`${service?.id}-${service?.name}`}
                value={service}
                className={modalClasses.itemsFontStyle}
              >
                {service?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.type')}
            </Typography>
          </Box>

          <Select
            MenuProps={menuProps}
            className={modalClasses.selectStyle}
            value={woType?.name ?? ''}
            onChange={woTypeChangeHandler}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_type')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {typeList.map(woType => (
              <MenuItem
                key={`${woType?.id}-${woType?.name}`}
                value={woType}
                className={modalClasses.itemsFontStyle}
              >
                {woType?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box className={modalClasses.buttonsContainer}>
          <Button
            className={modalClasses.modalCancelButton}
            variant="text"
            onClick={cancelHandler}
          >
            {t('field_forms.cancel')}
          </Button>
          <RoundedButton
            onClick={nextHandler}
            disabled={
              !formName || !trade || !service || !woType || !formDescription
            }
            className={modalClasses.modalNextButton}
          >
            {t('field_forms.next')}
          </RoundedButton>
        </Box>
      </Box>
    </Modal>
  )
}

export default NewFormModal
